/** Creates unique identifiers (injected; spec 1.7). */
export interface IdGenerator {
  /**
   * Returns a new identifier.
   *
   * @returns A UUID in lowercase canonical form.
   */
  next(): string;
}

/** Fills a byte array with random values (for example `crypto.getRandomValues`). */
export type RandomFill = (bytes: Uint8Array<ArrayBuffer>) => void;

const maxCounter = 0x0fff;

/**
 * Formats 16 bytes as a canonical UUID.
 *
 * @param bytes - The bytes.
 * @returns The UUID text.
 */
function formatUuid(bytes: Uint8Array): string {
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  const uuid = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;

  return uuid;
}

/**
 * Creates a monotonic UUIDv7 generator (RFC 9562, method 1: a 12-bit counter in `rand_a` keeps
 * identifiers created in the same millisecond ordered). Clock regressions reuse the last
 * timestamp, so identifiers never go backwards within one generator.
 *
 * @param nowMilliseconds - Clock in epoch milliseconds.
 * @param randomFill - Source of randomness.
 * @returns The generator.
 */
export function createUuidV7Generator(
  nowMilliseconds: () => number,
  randomFill: RandomFill,
): IdGenerator {
  let lastTimestamp = -1;
  let counter = 0;

  const generator: IdGenerator = {
    next() {
      let timestamp = Math.max(Math.floor(nowMilliseconds()), lastTimestamp);

      if (timestamp === lastTimestamp) {
        counter += 1;

        if (counter > maxCounter) {
          timestamp += 1;
          counter = 0;
        }
      } else {
        counter = 0;
      }

      lastTimestamp = timestamp;
      const bytes = new Uint8Array(16);
      randomFill(bytes);
      let remaining = timestamp;

      for (let index = 5; index >= 0; index -= 1) {
        bytes[index] = remaining % 256;
        remaining = Math.floor(remaining / 256);
      }

      bytes[6] = 0x70 | ((counter >> 8) & 0x0f);
      bytes[7] = counter & 0xff;
      bytes[8] = 0x80 | ((bytes[8] ?? 0) & 0x3f);
      const uuid = formatUuid(bytes);

      return uuid;
    },
  };

  return generator;
}
